import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'
import type { MemberInvite } from '../teams/inviteInput'

/**
 * The server's answer: whether the guest already had a WeHunt account (and so
 * got an in-app invitation) or was reached by email/SMS. Mirrors the mobile
 * client and the .NET `InviteHuntGuestResponse.Status` values.
 */
export type HuntGuestInviteStatus =
  | 'invited_in_app'
  | 'invited_email'
  | 'invited_sms'

/** What the API needs: the normalized invite plus the language to write it in. */
export type HuntGuestInviteRequest = MemberInvite & { locale: string }

/**
 * The single owner of the hunt-guest endpoint's path + payload shape. Both the
 * published-hunt mutation below and the plan wizard's finalize send-loop go
 * through this, so the wire contract can't drift between them. Takes the raw
 * `useApiFetch` call so a caller that only knows the hunt id after finalize (the
 * wizard) can use it without the hook, which binds the id at render.
 */
export function postHuntGuest(
  call: ReturnType<typeof useApiFetch>,
  groupId: string,
  huntPlanId: string,
  invite: HuntGuestInviteRequest,
) {
  return call<{ status: HuntGuestInviteStatus }>(
    `/clubhouse/groups/${encodeURIComponent(groupId)}/hunts/${encodeURIComponent(huntPlanId)}/guests`,
    { method: 'POST', body: JSON.stringify(invite) },
  )
}

/**
 * Invite a guest — someone outside the team — to a published hunt plan by email
 * or SMS (CLUB-972), posting the normalized invite (see {@link parseInviteInput})
 * to the same endpoint the mobile client uses. An address that belongs to a
 * WeHunt user becomes an in-app invitation; anyone else is emailed/SMSed a link.
 *
 * The address travels in the POST body, never in the path or query string
 * (RULE-006). Authorization (hunt manager) and all sending live on the backend;
 * the web client only renders the result. `locale` is the browser's active
 * language — the best guess at a language the recipient, usually not a user yet,
 * shares with the organiser (same reasoning as {@link useInviteMember}).
 */
export function useInviteHuntGuest(
  groupId: string | undefined,
  huntPlanId: string | undefined,
) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invite: HuntGuestInviteRequest) =>
      postHuntGuest(call, groupId ?? '', huntPlanId ?? '', invite),
    onSuccess: () => {
      // Refresh the hunt detail so an in-app invitee (the only guest the server
      // writes onto the hunt) shows up; the query key carries the viewer uid as
      // its last segment, so invalidate by the prefix to catch every viewer.
      void qc.invalidateQueries({
        queryKey: ['clubhouse', 'groups', groupId, 'hunts', huntPlanId, 'detail'],
      })
    },
    onError: (e) => {
      console.error('Failed to invite hunt guest', e)
    },
  })
}
